/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

import React from 'react';
import PropTypes from "prop-types";
export var BreadcrumbControlProps = function BreadcrumbControlProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
BreadcrumbControlProps.propTypes = {
  controlType: PropTypes.oneOf(["breadcrumbs"]).isRequired,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /**
     * Hides extra (above the max) breadcrumbs under a collapsed item as the window gets smaller.
     * Pass a custom #OuiBreadcrumbResponsiveMaxCount object to change the number of breadcrumbs to show at the particular breakpoints.
     * Omitting or passing a `0` value will show all breadcrumbs.
     *
     * Pass `false` to turn this behavior off.
     *
     * Default: `{ xs: 1, s: 2, m: 4 }`
     */
  responsive: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.any.isRequired]),
  /**
     * Forces all breadcrumbs to single line and
     * truncates each breadcrumb to a particular width,
     * except for the last item
     */
  truncate: PropTypes.bool,
  /**
     * Collapses the inner items past the maximum set here
     * into a single ellipses item
     */
  max: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf([null])]),
  /**
     * The array of individual #OuiBreadcrumb items
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    /**
       * Visible label of the breadcrumb
       */
    text: PropTypes.node.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    /**
       * Force a max-width on the breadcrumb text
       */
    truncate: PropTypes.bool
  }).isRequired).isRequired
};
export var ButtonControlProps = function ButtonControlProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
ButtonControlProps.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired
};
export var DividerControlProps = function DividerControlProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
DividerControlProps.propTypes = {
  controlType: PropTypes.oneOf(["divider"]).isRequired
};
export var IconControlTypeProps = function IconControlTypeProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
IconControlTypeProps.propTypes = {
  controlType: PropTypes.oneOf(["icon"]).isRequired,
  id: PropTypes.string.isRequired,
  iconType: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
export var IconButtonControlTypeProps = function IconButtonControlTypeProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
IconButtonControlTypeProps.propTypes = {
  controlType: PropTypes.oneOf(["icon"]).isRequired,
  id: PropTypes.string.isRequired,
  iconType: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
export var SpacerControlProps = function SpacerControlProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
SpacerControlProps.propTypes = {
  controlType: PropTypes.oneOf(["spacer"]).isRequired
};
export var TabControlProps = function TabControlProps() {
  return /*#__PURE__*/React.createElement("div", null);
};
TabControlProps.propTypes = {
  controlType: PropTypes.oneOf(["tab"]).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired
};
export var TextControlProps = () => <div />;
TextControlProps.propTypes = {
  controlType: PropTypes.oneOf(["text"]).isRequired,
  id: PropTypes.string.isRequired,
  text: PropTypes.node.isRequired,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
try {
  BreadcrumbControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'BreadcrumbControlProps',
    methods: [],
    props: {
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'BreadcrumbControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'BreadcrumbControl'
        }],
        required: true,
        type: {
          name: '"breadcrumbs"'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'BreadcrumbControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'BreadcrumbControl'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      responsive: {
        defaultValue: null,
        description: 'Hides extra (above the max) breadcrumbs under a collapsed item as the window gets smaller.\n' + 'Pass a custom #OuiBreadcrumbResponsiveMaxCount object to change the number of breadcrumbs to show at the particular breakpoints.\n' + 'Omitting or passing a `0` value will show all breadcrumbs.\n' + '\n' + 'Pass `false` to turn this behavior off.\n' + '\n' + 'Default: `{ xs: 1, s: 2, m: 4 }`',
        name: 'responsive',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | OuiBreadcrumbResponsiveMaxCount'
        }
      },
      truncate: {
        defaultValue: null,
        description: 'Forces all breadcrumbs to single line and\n' + 'truncates each breadcrumb to a particular width,\n' + 'except for the last item',
        name: 'truncate',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      max: {
        defaultValue: null,
        description: 'Collapses the inner items past the maximum set here\n' + 'into a single ellipses item',
        name: 'max',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      breadcrumbs: {
        defaultValue: null,
        description: 'The array of individual #OuiBreadcrumb items',
        name: 'breadcrumbs',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/breadcrumbs/breadcrumbs.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'OuiBreadcrumb[]'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  ButtonControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'ButtonControlProps',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'ButtonControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'ButtonControl'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      label: {
        defaultValue: null,
        description: '',
        name: 'label',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'ButtonControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'ButtonControl'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      color: {
        defaultValue: null,
        description: 'Any of our named colors.\n' + '**`secondary` color is DEPRECATED, use `success` instead**',
        name: 'color',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonColor',
          value: [{
            value: '"text"'
          }, {
            value: '"secondary"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Any `type` accepted by OuiIcon',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '`disabled` is also allowed',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Force disables the button and changes the icon to a loading spinner',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      contentProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the button's content",
        name: 'contentProps',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'OuiButtonContentType'
        }
      },
      iconSide: {
        defaultValue: null,
        description: 'Can only be one side `left` or `right`',
        name: 'iconSide',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonContentIconSide',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      textProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the content's text/children only (not icon)",
        name: 'textProps',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: "HTMLAttributes<HTMLSpanElement> & CommonProps & { ref?: Ref<HTMLSpanElement>; 'data-text'?: string; }"
        }
      },
      iconSize: {
        defaultValue: null,
        description: '',
        name: 'iconSize',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      iconGap: {
        defaultValue: null,
        description: 'The gap between the icon and the content',
        name: 'iconGap',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonContentIconGap',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"none"'
          }]
        }
      },
      fill: {
        defaultValue: null,
        description: 'Make button a solid color for prominence',
        name: 'fill',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: 'Extends the button to 100% width',
        name: 'fullWidth',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      minWidth: {
        defaultValue: null,
        description: 'Override the default minimum width',
        name: 'minWidth',
        parent: {
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button.tsx',
          name: 'OuiButtonProps'
        }],
        required: false,
        type: {
          name: 'MinWidth<string | number>'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  DividerControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'DividerControlProps',
    methods: [],
    props: {
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'DividerControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'DividerControl'
        }],
        required: true,
        type: {
          name: '"divider"'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  IconControlTypeProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'IconControlTypeProps',
    methods: [],
    props: {
      title: {
        defaultValue: null,
        description: 'Descriptive title for naming the icon based on its use',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: null,
        description: "One of OUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.\n" + 'Note that coloring only works if your SVG is removed of fill attributes.\n' + '**`secondary` color is DEPRECATED, use `success` instead**',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'SVGAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      size: {
        defaultValue: null,
        description: 'Note that every size other than `original` assumes the provided SVG sits on a square viewbox.',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "xxl" | "original"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"xxl"'
          }, {
            value: '"original"'
          }]
        }
      },
      titleId: {
        defaultValue: null,
        description: 'A unique identifier for the title element',
        name: 'titleId',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onIconLoad: {
        defaultValue: null,
        description: 'Callback when the icon has been loaded & rendered',
        name: 'onIconLoad',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: '"icon"'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: '',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<Element>'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  IconButtonControlTypeProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'IconButtonControlTypeProps',
    methods: [],
    props: {
      color: {
        defaultValue: null,
        description: 'Any of the named color palette options.\n' + '**`subdued` set to be DEPRECATED, use `text` instead**',
        name: 'color',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'OuiButtonIconColor',
          value: [{
            value: '"text"'
          }, {
            value: '"subdued"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: '',
        name: 'aria-labelledby',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      size: {
        defaultValue: null,
        description: 'Overall size of button.\nMatches the sizes of other OuiButtons',
        name: 'size',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      iconSize: {
        defaultValue: null,
        description: 'Size of the icon only.\n' + 'This will not affect the overall size of the button',
        name: 'iconSize',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "xxl" | "original"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"xxl"'
          }, {
            value: '"original"'
          }]
        }
      },
      display: {
        defaultValue: null,
        description: 'Sets the display style for matching other OuiButton types.\n' + '`base` is equivelant to a typical OuiButton\n' + '`fill` is equivelant to a filled OuiButton\n' + '`empty` (default) is equivelant to an OuiButtonEmpty',
        name: 'display',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"base" | "empty" | "fill"',
          value: [{
            value: '"base"'
          }, {
            value: '"empty"'
          }, {
            value: '"fill"'
          }]
        }
      },
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: '"icon"'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: '',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'IconControlProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<Element>'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  SpacerControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'SpacerControlProps',
    methods: [],
    props: {
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'SpacerControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'SpacerControl'
        }],
        required: true,
        type: {
          name: '"spacer"'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  TabControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'TabControlProps',
    methods: [],
    props: {
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        }],
        required: true,
        type: {
          name: '"tab"'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      label: {
        defaultValue: null,
        description: '',
        name: 'label',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TabControl'
        }],
        required: true,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}
try {
  TextControlProps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/control_bar/props.tsx',
    description: '',
    displayName: 'TextControlProps',
    methods: [],
    props: {
      controlType: {
        defaultValue: null,
        description: '',
        name: 'controlType',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        }],
        required: true,
        type: {
          name: '"text"'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      text: {
        defaultValue: null,
        description: '',
        name: 'text',
        parent: {
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        },
        declarations: [{
          fileName: 'src/src/components/control_bar/control_bar.tsx',
          name: 'TextControl'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['BreadcrumbControl', 'CommonProps', 'ButtonControl', 'OuiButtonProps', 'OuiButtonContentProps', 'DividerControl', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'IconControlProps', 'OuiButtonIconProps', 'SpacerControl', 'TabControl', 'ButtonHTMLAttributes', 'HTMLAttributes', 'TextControl']
  };
} catch (e) {}